import React from 'react';
import { Box, Typography } from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import serviceIcon from '../../assets/icons/service_icon_get_started.png';
import customerIcon from '../../assets/icons/customer_get_started_icon.png';
import { Opacity } from '@mui/icons-material';

interface GetStartedProps {
  /**
   * Optional custom class name for the component
   */
  className?: string;
  /**
   * Optional custom style overrides
   */
  sx?: any;
}

/**
 * GetStarted Component
 *
 * A reusable CTA component that directs users to signup with pre-selected roles.
 * - "Become a Provider" button navigates to signup with handyman role
 * - "Find a Professional" button navigates to signup with customer role
 */
export const GetStarted: React.FC<GetStartedProps> = ({ className, sx }) => {
  const navigate = useNavigate();

  const handleBecomeProvider = () => {
    trackEvent("get_started_become_provider");
    navigate('/sign-up?role=handyman');
  };

  const handleFindProfessional = () => {
    trackEvent("get_started_find_professional");
    navigate('/sign-up?role=customer');
  };

  return (
    <GetStartedContainer className={className} sx={sx}>
      {/* Heading Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: '24px',
            lineHeight: '133.4%',
            color: (theme) =>
              theme.palette.mode === 'dark' ? '#E5E5E5' : '#393939',
            textAlign: 'center',
          }}
        >
          Ready to get started?
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '175%',
            letterSpacing: '0.15px',
            color: (theme) =>
              theme.palette.mode === 'dark' ? '#C4C4C4' : '#1A1A1A',
            textAlign: 'center',
            maxWidth: '589px',
          }}
        >
          Whether you have a to-do list to finish or skills to share, we have a
          place for you.
        </Typography>
      </Box>

      {/* Buttons Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: { xs: 'center', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: '8px', sm: '32px' },
            width: '100%',
            maxWidth: '908px',
            flexWrap: 'wrap',
            '& button': {
              '@media (max-width:399px)': {
                width: '100%',
              },
            },
          }}
        >
          {/* Become a Provider Button (Tertiary) */}
          <TertiaryButton onClick={handleBecomeProvider}>
            <Box
              component='img'
              src={serviceIcon}
              alt='Service Provider'
              sx={{
                width: { xs: '16px', sm: '24px' },
                height: { xs: '16px', sm: '24px' },
                marginRight: { xs: '4px', sm: '8px' },
                filter: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'brightness(0) invert(1) opacity(0.9)'
                    : 'none',
              }}
            />
            Become a Provider
          </TertiaryButton>

          {/* Find a Professional Button (Secondary) */}
          <SecondaryButton onClick={handleFindProfessional}>
            <Box
              component='img'
              src={customerIcon}
              alt='Find Professional'
              sx={{
                width: { xs: '16px', sm: '24px' },
                height: { xs: '16px', sm: '24px' },
                marginRight: { xs: '4px', sm: '8px' },
                filter: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'brightness(0) invert(1)'
                    : 'none',
              }}
            />
            Find a Professional
          </SecondaryButton>
        </Box>

        {/* Caption */}
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '166%',
            letterSpacing: '0.4px',
            color: (theme) =>
              theme.palette.mode === 'dark' ? '#B0B0B0' : '#393939',
            textAlign: 'center',
          }}
        >
          Free to join. Verified professionals. Secure payments.
        </Typography>
      </Box>
    </GetStartedContainer>
  );
};

// Main Container - Glassmorphic card with exact Figma specs
const GetStartedContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '16px 24px 24px',
  gap: '24px',
  width: '100%',
  maxWidth: '956px',
  background:
    theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : '#E5E5DC',
  boxShadow:
    theme.palette.mode === 'dark'
      ? `
      0px 4px 20px rgba(0, 0, 0, 0.5),
      0px 2px 8px rgba(0, 0, 0, 0.3),
      inset 0px 1px 1px rgba(255, 255, 255, 0.05),
      inset 0px -1px 1px rgba(0, 0, 0, 0.5)
    `
      : `
      10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2),
      -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05),
      14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1),
      -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3),
      inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3),
      inset 1.57925px 1.57925px 1.69811px #FFFFFF
    `,
  borderRadius: '32px',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : 'none',

  // Responsive adjustments
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: '16px 20px 20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 16px 20px',
    gap: '20px',
  },
}));

// Tertiary Button - Grey/transparent style for "Become a Provider"
const TertiaryButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '10px 28px',
  gap: '8px',
  minWidth: 'auto',
  width: '235px',
  height: '48px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(80, 80, 80, 0.6)'
      : 'rgba(153, 153, 153, 0.3)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? `
      0px 2px 8px rgba(0, 0, 0, 0.3),
      0px 1px 4px rgba(0, 0, 0, 0.2),
      inset 0px 1px 1px rgba(255, 255, 255, 0.1),
      inset 0px -1px 1px rgba(0, 0, 0, 0.3)
    `
      : `
      0px 2px 8px rgba(0, 0, 0, 0.1),
      0px 1px 4px rgba(0, 0, 0, 0.06),
      inset 0px 1px 2px rgba(255, 255, 255, 0.4),
      inset 0px -1px 2px rgba(0, 0, 0, 0.1)
    `,
  backdropFilter: 'blur(8px)',
  borderRadius: '100px',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : 'none',
  textTransform: 'none' as const,
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '1.5',
  color: theme.palette.mode === 'dark' ? '#E5E5E5' : '#393939',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',

  '&:hover': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(90, 90, 90, 0.7)'
        : 'rgba(153, 153, 153, 0.4)',
    transform: 'translateY(-2px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `
        0px 4px 12px rgba(0, 0, 0, 0.4),
        0px 2px 6px rgba(0, 0, 0, 0.3),
        inset 0px 1px 1px rgba(255, 255, 255, 0.1),
        inset 0px -1px 1px rgba(0, 0, 0, 0.3)
      `
        : `
        0px 4px 12px rgba(0, 0, 0, 0.15),
        0px 2px 6px rgba(0, 0, 0, 0.1),
        inset 0px 1px 2px rgba(255, 255, 255, 0.4),
        inset 0px -1px 2px rgba(0, 0, 0, 0.1)
      `,
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '5px 10px',
    width: '164px',
    height: '32px',
    minWidth: 'auto',
  },
}));

// Secondary Button - Dark blue style for "Find a Professional"
export const SecondaryButton = styled(Button)<ButtonProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '10px 28px',
  gap: '8px',
  minWidth: 'auto',
  width: '235px',
  height: '48px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 92, 173, 0.7)'
      : 'rgba(0, 36, 58, 0.8)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? `
      0px 2px 8px rgba(0, 0, 0, 0.4),
      0px 1px 4px rgba(0, 0, 0, 0.3),
      inset 0px 1px 1px rgba(255, 255, 255, 0.1),
      inset 0px -1px 1px rgba(0, 0, 0, 0.3)
    `
      : `
      0px 2px 8px rgba(0, 0, 0, 0.2),
      0px 1px 4px rgba(0, 0, 0, 0.1),
      inset 0px 1px 2px rgba(255, 255, 255, 0.15),
      inset 0px -1px 2px rgba(0, 0, 0, 0.2)
    `,
  backdropFilter: 'blur(12px)',
  borderRadius: '100px',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : 'none',
  textTransform: 'none' as const,
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#FFFFFF',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  '&:hover': {
    background:
      theme.palette.mode === 'dark' ? '#4A7DB0' : 'rgba(0, 36, 58, 0.8)',
    transform: 'translateY(-2px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `
        0px 4px 12px rgba(0, 0, 0, 0.5),
        0px 2px 6px rgba(0, 0, 0, 0.4),
        inset 0px 1px 1px rgba(255, 255, 255, 0.1),
        inset 0px -1px 1px rgba(0, 0, 0, 0.3)
      `
        : `
        0px 4px 12px rgba(0, 0, 0, 0.25),
        0px 2px 6px rgba(0, 0, 0, 0.15),
        inset 0px 1px 2px rgba(255, 255, 255, 0.15),
        inset 0px -1px 2px rgba(0, 0, 0, 0.2)
      `,
  },
  '&.Mui-disabled': {
    color: '#777777',
    background: 'rgba(59, 59, 59, 0.18)',
  },
  '&:focus-within, &:focus, &:focus-visible': {
    color: ' #E2F7FF',
    background: 'rgba(59, 59, 59, 0.18)',
    border: '1px solid #E2F7FF',
    boxShadow: `
      1px 1px 0.5px -1px #C3C3C34D inset,
      -1px -1px 0px -0.5px #FFFFFF80 inset,
      1px 1px 0px -0.5px #FFFFFF99 inset,
      0px 1px 8px 0px #0000001F,
      0px 0px 2px 0px #0000001A
    `,
  },
  '&:active': {
    background: 'rgba(0, 36, 58, 0.8)',
    color: 'rgba(0, 255, 255, 1)',
    border: 0,
    '& svg path': {
      fill: 'rgba(0, 255, 255, 1)',
    },
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '5px 10px',
    width: '164px',
    height: '32px',
    minWidth: 'auto',
  },
}));

export default GetStarted;
